import { useEffect, useRef, useState, type FormEvent } from 'react'
import { apiFetch } from '../../lib/api'
import type { HeldBookItem, HeldSourceInterpretationOption } from './bookWorkApi'

interface Props {
  item: HeldBookItem
  option: HeldSourceInterpretationOption
  fundId: string
  onPendingChange: (pending: boolean) => void
  onSaved: () => void
}

interface Receipt {
  command_id: string
  interpretation_id: string
  item_id: string
  actor_id: string
  field: string
  answer: string
  revision: string
  source_sha256: string
  // null when the call was already published: the answer wakes nothing then.
  continuation_event_id: string | null
  financial_authority: false
  standing_balance_created: false
  evidence_waived: false
  book_granted: false
}

type Choice = '' | 'call_only' | 'request_evidence'

export default function HeldSourceInterpretationAction({
  item,
  option,
  fundId,
  onPendingChange,
  onSaved,
}: Props) {
  // No default: the officer states the reading; nothing is inferred.
  const [choice, setChoice] = useState<Choice>('')
  const [pending, setPending] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const active = useRef(false)
  const inFlight = useRef(false)
  const attempt = useRef<{ input: string; id: string } | null>(null)
  useEffect(() => {
    active.current = true
    return () => {
      active.current = false
    }
  }, [])

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (inFlight.current || saved || choice !== 'call_only') return
    const input = JSON.stringify({
      revision: item.revision,
      scope: { fund_id: fundId },
      field: option.field,
      answer: option.answer,
      citation: option.citation,
      source_sha256: option.source_sha256,
      source_position_fingerprint: option.source_position_fingerprint,
    })
    if (attempt.current?.input !== input) attempt.current = { input, id: crypto.randomUUID() }
    const command = attempt.current
    inFlight.current = true
    setPending(true)
    setError('')
    onPendingChange(true)
    try {
      const result = await apiFetch<Receipt>(
        `/api/rk/decks/group/${encodeURIComponent(item.group_id)}/held/${encodeURIComponent(item.item_id)}/source-interpretation`,
        {
          method: 'POST',
          body: JSON.stringify({ ...JSON.parse(command.input), command_id: command.id }),
        }
      )
      if (
        result.command_id !== command.id ||
        result.item_id !== item.item_id ||
        result.revision !== item.revision ||
        result.field !== option.field ||
        result.answer !== option.answer ||
        result.source_sha256 !== option.source_sha256 ||
        typeof result.interpretation_id !== 'string' ||
        !result.interpretation_id ||
        typeof result.actor_id !== 'string' ||
        !result.actor_id ||
        result.financial_authority !== false ||
        result.standing_balance_created !== false ||
        result.evidence_waived !== false ||
        result.book_granted !== false
      )
        throw new Error(
          'The source interpretation could not be confirmed. Refresh before continuing.'
        )
      if (active.current) {
        setSaved(true)
        onSaved()
      }
    } catch (failure) {
      if (active.current)
        setError(
          failure instanceof Error ? failure.message : 'Could not record the source interpretation.'
        )
    } finally {
      inFlight.current = false
      if (active.current) {
        setPending(false)
        onPendingChange(false)
      }
    }
  }

  if (saved)
    return (
      <p role="status">Source interpretation recorded. Next: review and Book the capital call.</p>
    )
  return (
    <form onSubmit={submit} aria-label="Answer the source question">
      <fieldset disabled={pending}>
        <legend>Your reading of the cited notice</legend>
        <label>
          <input
            type="radio"
            name={`source-interpretation:${item.item_id}:${option.field}`}
            checked={choice === 'call_only'}
            onChange={() => setChoice('call_only')}
          />
          {option.label}
        </label>
        {/* Offered only when a request can actually be prepared below. */}
        {item.request_draft_available ? (
          <label>
            <input
              type="radio"
              name={`source-interpretation:${item.item_id}:${option.field}`}
              checked={choice === 'request_evidence'}
              onChange={() => setChoice('request_evidence')}
            />
            Request independent standing evidence.
          </label>
        ) : null}
      </fieldset>
      <p>
        This records your reading of the source only. It books nothing and states no balance. The
        capital call is then reviewed and Booked separately.
      </p>
      {choice === 'request_evidence' ? (
        <p role="status">This question stays open. Prepare the request below.</p>
      ) : null}
      <button type="submit" disabled={pending || choice !== 'call_only'}>
        {pending ? 'Recording…' : 'Record source interpretation'}
      </button>
      <button type="button" disabled={pending || !choice} onClick={() => setChoice('')}>
        Keep for review
      </button>
      {error ? <p role="alert">{error}</p> : null}
    </form>
  )
}
